import React, { memo, useContext, useEffect, useMemo, useRef } from 'react'
import { Formik } from 'formik'
import isEqual from 'lodash/isEqual'
/* webpack-strip-block:removed */
import { LeadFormSpecContext } from '@app-src/data/lead-form-spec/fetchContexts'
import { LeadFormSpecResponseType } from '@app-src/data/lead-form-spec/types'
import { AccountMakeBrandContext } from '@app-src/data/make-brand/fetchContexts'
import { MakeBrandType } from '@app-src/data/make-brand/types'
import { transformMakeBrandIntoMvhColor } from '@app-src/data/make-brand/utils'
import { TEXT_ME } from './constants'
import {
  MODAL_LEAD_FORM_FIELDS_STANDARD,
  MODAL_LEAD_FORM_FIELDS_TEXT_ME_THIS_INFO,
  getModalLeadFormFieldInitialValues,
  validateModalLeadFormFieldValues
} from './fields'
import Contents from './Contents'
import { FormSubmitCallbackType, FormContentPropsType } from './types'

const MVH_COLORS_DEFAULT = {}

// this component owns the Formik portion of the modal form
export const FormContentComponent: React.FC<FormContentPropsType> = ({
  accountName,
  desiredFields,
  /* webpack-strip-block:removed */
  formName,
  mvhColors,
  onSubmit,
  requirePhone,
  setMvhColors,
  ...rest
}) => {
  // hooks, state, and refs
  /////////////////////////
  const accountMakeBrand = useContext(AccountMakeBrandContext)
    .response as MakeBrandType
  const formDataSpec = useContext(LeadFormSpecContext)
    .response as LeadFormSpecResponseType
  const performedInitialValidationRef = useRef<boolean>(false)

  // memos
  ////////

  // we need to get the initial mvh colors at mount time, since we will restore them at unmount time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mvhColorsAtMount = useMemo(() => mvhColors, [])

  const desiredFieldsUse = useMemo(
    () =>
      desiredFields ||
      (formName && formName.toLowerCase().includes(TEXT_ME)
        ? MODAL_LEAD_FORM_FIELDS_TEXT_ME_THIS_INFO
        : MODAL_LEAD_FORM_FIELDS_STANDARD),
    [desiredFields, formName]
  )

  const brandColorsUse = useMemo(
    () =>
      mvhColors ||
      (accountMakeBrand && mvhColors !== null
        ? transformMakeBrandIntoMvhColor(accountMakeBrand)
        : MVH_COLORS_DEFAULT),
    [accountMakeBrand, mvhColors]
  )

  // effects
  //////////

  useEffect(() => {
    if (setMvhColors) {
      setMvhColors(brandColorsUseCur =>
        isEqual(brandColorsUse, brandColorsUseCur)
          ? brandColorsUseCur
          : brandColorsUse
      )
    }
    return () => {
      // restore original colors at unmount time
      if (
        /* webpack-strip-block:removed */
        setMvhColors
      ) {
        setMvhColors(mvhColorsAtMount)
      }
    }
  }, [brandColorsUse, mvhColorsAtMount, setMvhColors])

  return (
    <Formik
      initialValues={getModalLeadFormFieldInitialValues(
        desiredFieldsUse,
        formDataSpec,
        /* webpack-strip-block:removed */
      )}
      validate={validateModalLeadFormFieldValues(
        desiredFieldsUse,
        formDataSpec,
        requirePhone
      )}
      onSubmit={onSubmit as FormSubmitCallbackType}
    >
      {formikProps => {
        if (!performedInitialValidationRef.current) {
          performedInitialValidationRef.current = true
          formikProps.validateForm()
        }
        return (
          // @ts-ignore
          <Contents
            accountName={accountName}
            desiredFields={desiredFieldsUse}
            formDataSpec={formDataSpec}
            formName={formName}
            mvhColors={brandColorsUse}
            {...rest}
            {...formikProps}
          />
        )
      }}
    </Formik>
  )
}

export default memo(FormContentComponent)
